import React from 'react'
import { Stack, Typography, FormControl, FormControlLabel, Radio, RadioGroup } from '@mui/material'
import { Controller, useFormContext } from 'react-hook-form'
import { z } from 'zod'
import { selectCostHandlingMethodSchemaFields } from './SelectCostHandlingMethod'
import { CampaignInputComponent, CampaignInputComponentProps } from './CampaignInputComponent'
import { FormatVersion } from '~/generated/graphql'
import { BURDEN_RATE_VALUES, type BurdenRate } from './constants/burdenRate'

const labelOptions = BURDEN_RATE_VALUES.map((value) => ({
  label:
    value === 0
      ? '100%（インフルエンサーの負担はありません）'
      : `${100 - value}%（インフルエンサーが購入代金の${value}%を負担します）`,
  value: String(value),
}))

export const getBurdenRateLabel = (rate: number): string => {
  const found = labelOptions.find((o) => Number(o.value) === rate)
  return found?.label ?? labelOptions[0]?.label ?? ''
}

// BURDEN_RATE_VALUESから自動的に厳密な型のzodスキーマを生成
// 要素の追加・削除に対応できるよう動的に生成
const createBurdenRateSchema = () => {
  const [first, second, ...rest] = BURDEN_RATE_VALUES
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (second === undefined) {
    return z.literal(first)
  }
  return z.union([z.literal(first), z.literal(second), ...rest.map((value) => z.literal(value))] as [
    z.ZodLiteral<BurdenRate>,
    z.ZodLiteral<BurdenRate>,
    ...z.ZodLiteral<BurdenRate>[],
  ])
}

export const influencerBurdenRateSchemaFields = {
  influencerBurdenRate: createBurdenRateSchema(),
}

export const influencerBurdenRateSchema = z.object({
  ...influencerBurdenRateSchemaFields,
  ...selectCostHandlingMethodSchemaFields,
})

type Props = CampaignInputComponentProps

export const BurdenRateSelector = ({ description, tooltip }: Props) => {
  const { control } = useFormContext()

  return (
    <CampaignInputComponent
      title="立替費用還元率"
      required={true}
      description={description}
      tooltip={tooltip}
      compatibleFormatVersions={[FormatVersion.V4Campaign]}
    >
      <Stack>
        <Typography variant="body2">
          還元率が低いほどインフルエンサーの応募数が減少する傾向にありますので予めご理解ください。
        </Typography>
      </Stack>
      <FormControl component="fieldset">
        <Controller
          name="influencerBurdenRate"
          control={control}
          render={({ field: { ref, value, onChange, ...field } }) => (
            <RadioGroup
              {...field}
              value={value === undefined || value === null ? '' : String(value)}
              onChange={(_, v) => onChange(v === '' ? undefined : Number(v))}
            >
              {labelOptions.map((opt) => (
                <FormControlLabel key={opt.value} value={opt.value} control={<Radio ref={ref} />} label={opt.label} />
              ))}
            </RadioGroup>
          )}
        />
      </FormControl>
    </CampaignInputComponent>
  )
}

export default BurdenRateSelector
